import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  DOCUMENT,
  ElementRef,
  inject,
  input,
  NgZone,
  afterNextRender,
  computed,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ThinkingOrbComponent } from '../thinking-orb/thinking-orb';

type BusyOverlayPlacement = 'card' | 'tableRow';

/**
 * Card: projects into `app-resource-card` via `[cardOverlay]` (default).
 * Table row: same frosted + spinner treatment; uses fixed positioning aligned to the host row (`tr`).
 *
 * `[thinking]="true"` swaps the Material-icon spinner for the canvas
 * thinking-orb — reserved for AI-generation waits (document generation,
 * assessment generation). Non-AI busy states (export, upload, download)
 * keep the icon path so the two semantics stay visually distinct.
 */
@Component({
  selector: 'app-busy-overlay',
  imports: [MatIconModule, ThinkingOrbComponent],
  template: `
    @if (thinking()) {
      <app-thinking-orb state="working" [size]="orbSize()" />
    } @else {
      <mat-icon class="spinner-icon">{{ icon() }}</mat-icon>
    }
    <span>{{ message() }}</span>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.card-loading-overlay]': "placement() === 'card'",
    '[class.table-row-busy-overlay]': "placement() === 'tableRow'",
    '[attr.cardOverlay]': "placement() === 'card' ? '' : null",
    '[attr.role]': "placement() === 'tableRow' ? 'status' : null",
    '[attr.aria-live]': "placement() === 'tableRow' ? 'polite' : null",
  },
})
export class BusyOverlayComponent {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly doc = inject(DOCUMENT);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  icon = input.required<string>();
  message = input.required<string>();
  placement = input<BusyOverlayPlacement>('card');
  /** Swap the Material-icon spinner for the canvas thinking-orb.
   *  Set to `true` for AI-generation waits; leave false for export /
   *  upload / download busy states. */
  thinking = input<boolean>(false);

  /** Orb dimension — table-row overlays are narrow, cards have room. */
  protected readonly orbSize = computed<20 | 64>(() => (this.placement() === 'card' ? 64 : 20));

  constructor() {
    afterNextRender(() => {
      if (this.placement() !== 'tableRow') {
        return;
      }

      const hostEl = this.host.nativeElement;
      const row = hostEl.closest('tr');
      const win = this.doc.defaultView;
      if (!row || !win) {
        return;
      }

      const clearInlineGeometry = (): void => {
        hostEl.style.removeProperty('position');
        hostEl.style.removeProperty('top');
        hostEl.style.removeProperty('left');
        hostEl.style.removeProperty('width');
        hostEl.style.removeProperty('height');
      };

      this.ngZone.runOutsideAngular(() => {
        const sync = (): void => {
          const r = row.getBoundingClientRect();
          hostEl.style.setProperty('position', 'fixed');
          hostEl.style.setProperty('top', `${Math.round(r.top)}px`);
          hostEl.style.setProperty('left', `${Math.round(r.left)}px`);
          hostEl.style.setProperty('width', `${Math.round(r.width)}px`);
          hostEl.style.setProperty('height', `${Math.round(r.height)}px`);
        };

        sync();

        const ro = new ResizeObserver(() => sync());
        ro.observe(row);

        const scrollTargets: (HTMLElement | Window)[] = [win];
        for (let el: HTMLElement | null = hostEl.parentElement; el; el = el.parentElement) {
          const { overflowY, overflowX } = getComputedStyle(el);
          if (/(auto|scroll|overlay)/.test(overflowY) || /(auto|scroll|overlay)/.test(overflowX)) {
            scrollTargets.push(el);
          }
        }
        for (const t of scrollTargets) {
          t.addEventListener('scroll', sync, true);
        }
        win.addEventListener('resize', sync);

        this.destroyRef.onDestroy(() => {
          ro.disconnect();
          for (const t of scrollTargets) {
            t.removeEventListener('scroll', sync, true);
          }
          win.removeEventListener('resize', sync);
          clearInlineGeometry();
        });
      });
    });
  }
}
